/**
 * Executive assistants should not get project-work bells unless @mentioned.
 * Keep in sync with `functions/src/eaProjectBellPolicy.ts`.
 */

export function userHasExecutiveAssistantDesignation(designations: unknown): boolean {
  return Array.isArray(designations) && designations.map(String).includes('executive_assistant');
}

/** Bell types that mean the EA was explicitly @mentioned. */
export const EA_MENTION_BELL_TYPES = new Set([
  'mention',
  'task_mention',
  'board_mention',
]);

/**
 * Project Hub / portal / deal construction events. Ops Hub (`opsTaskId`) and
 * org-ops (billing, payables, leads, hiring) are not in this set.
 */
export const EA_PROJECT_SCOPED_BELL_TYPES = new Set([
  'project_file_version',
  'pdf_comment',
  'pdf_external_comment',
  'plan_review_complete',
  'task_comment',
  'external_comment',
  'architect_comment',
  'task_assigned',
  'ext_task_assigned',
  'ext_status_changed',
  'ext_action_required',
  'ext_task_completed',
  'onboarding_action_needed',
  'board_comment',
  'portal_feedback',
  'portal_upload',
  'portal_annotation',
  'portal_deliverable_approval',
  'portal_walkthrough_summary',
  'proposal_comment',
  'supplier_rfq_upload',
  'deal_won',
  'change_order_approved',
  'questionnaire_completed',
]);

export function isProjectScopedBell(notif: {
  type?: string;
  projectId?: string | null;
  opsTaskId?: string | null;
}): boolean {
  const type = String(notif.type || '');
  if (!type) return false;
  if (EA_MENTION_BELL_TYPES.has(type)) return false;
  if (typeof notif.opsTaskId === 'string' && notif.opsTaskId.trim()) return false;
  if (EA_PROJECT_SCOPED_BELL_TYPES.has(type)) return true;
  if (typeof notif.projectId === 'string' && notif.projectId.trim()) return true;
  return false;
}

export function shouldSkipEaProjectBell(args: {
  designations?: unknown;
  type?: string;
  projectId?: string | null;
  opsTaskId?: string | null;
}): boolean {
  if (!userHasExecutiveAssistantDesignation(args.designations)) return false;
  return isProjectScopedBell(args);
}

export function filterBellNotificationsForUser<
  T extends { type?: string; projectId?: string | null; opsTaskId?: string | null },
>(items: T[], designations: unknown): T[] {
  return items.filter(
    (n) =>
      !shouldSkipEaProjectBell({
        designations,
        type: n.type,
        projectId: n.projectId,
        opsTaskId: n.opsTaskId,
      }),
  );
}
